import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useAuth } from '@/hooks/useAuth'
import { useReservations } from '@/hooks/useReservations'
import { useVehicles } from '@/hooks/useVehicles'
import {Calendar, ChevronLeft, ChevronRight, Car, Clock, User, Phone, MapPin} from 'lucide-react'

export default function AgendaPage() {
  const { isAdmin } = useAuth()
  const navigate = useNavigate()
  const { reservations, loading: reservationsLoading, fetchReservations } = useReservations()
  const { vehicles, fetchVehicles } = useVehicles()
  
  const [currentDate, setCurrentDate] = useState(new Date())
  const [viewMode, setViewMode] = useState<'week' | 'month'>('week')

  useEffect(() => {
    if (!isAdmin) {
      navigate('/')
      return
    }
    fetchReservations({ sort: { startDate: 1 } })
    fetchVehicles()
  }, [isAdmin])

  const getVehicleById = (vehicleId: string) => {
    return vehicles.find(v => v._id === vehicleId)
  }

  const getWeekDays = (date: Date) => {
    const start = new Date(date)
    start.setDate(start.getDate() - start.getDay() + 1) // Lundi
    const days = []
    for (let i = 0; i < 7; i++) {
      const day = new Date(start)
      day.setDate(start.getDate() + i)
      days.push(day)
    }
    return days
  }

  const getMonthDays = (date: Date) => {
    const year = date.getFullYear()
    const month = date.getMonth()
    const firstDay = new Date(year, month, 1)
    const lastDay = new Date(year, month + 1, 0)
    const days = []
    
    // Jours du mois précédent
    const firstDayOfWeek = firstDay.getDay() || 7
    for (let i = firstDayOfWeek - 1; i > 0; i--) {
      const day = new Date(year, month, 1 - i)
      days.push({ date: day, isCurrentMonth: false })
    }
    
    // Jours du mois actuel
    for (let i = 1; i <= lastDay.getDate(); i++) {
      days.push({ date: new Date(year, month, i), isCurrentMonth: true })
    }
    
    // Jours du mois suivant
    const remaining = 42 - days.length
    for (let i = 1; i <= remaining; i++) {
      days.push({ date: new Date(year, month + 1, i), isCurrentMonth: false })
    }
    
    return days
  }

  const getReservationsForDay = (day: Date) => {
    return reservations.filter(res => {
      const start = new Date(res.startDate)
      const end = new Date(res.endDate)
      const dayStart = new Date(day.setHours(0, 0, 0, 0))
      const dayEnd = new Date(day.setHours(23, 59, 59, 999))
      return (start <= dayEnd && end >= dayStart)
    })
  }

  const previousPeriod = () => {
    const newDate = new Date(currentDate)
    if (viewMode === 'week') {
      newDate.setDate(newDate.getDate() - 7)
    } else {
      newDate.setMonth(newDate.getMonth() - 1)
    }
    setCurrentDate(newDate)
  }

  const nextPeriod = () => {
    const newDate = new Date(currentDate)
    if (viewMode === 'week') {
      newDate.setDate(newDate.getDate() + 7)
    } else {
      newDate.setMonth(newDate.getMonth() + 1)
    }
    setCurrentDate(newDate)
  }

  const getStatusColor = (status: string) => {
    const colors: Record<string, string> = {
      pending: 'bg-yellow-400 border-yellow-500',
      confirmed: 'bg-blue-400 border-blue-500',
      paid: 'bg-green-400 border-green-500',
      active: 'bg-purple-400 border-purple-500',
      completed: 'bg-gray-400 border-gray-500',
      cancelled: 'bg-red-400 border-red-500',
    }
    return colors[status] || 'bg-gray-400 border-gray-500'
  }

  const weekDays = viewMode === 'week' ? getWeekDays(currentDate) : []
  const monthDays = viewMode === 'month' ? getMonthDays(currentDate) : []

  if (!isAdmin) return null

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-900 via-black to-gray-900">
      {/* Header */}
      <div className="bg-black text-white py-12 border-b border-yellow-400/20 shadow-2xl">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <button
            onClick={() => navigate('/admin')}
            className="flex items-center gap-2 text-yellow-400 hover:text-yellow-300 mb-4 transition-colors"
          >
            <ChevronLeft className="w-5 h-5" />
            Retour au tableau de bord
          </button>
          
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <div className="bg-gradient-to-br from-yellow-400 to-yellow-500 p-4 rounded-2xl shadow-lg">
                <Calendar className="w-10 h-10 text-black" />
              </div>
              <div>
                <h1 className="text-4xl font-black bg-gradient-to-r from-yellow-400 via-yellow-300 to-yellow-400 bg-clip-text text-transparent">
                  Agenda des Réservations
                </h1>
                <p className="text-gray-400 mt-1 text-lg">
                  {viewMode === 'week' ? 'Vue hebdomadaire' : 'Vue mensuelle'}
                </p>
              </div>
            </div>
            
            {/* View Toggle */}
            <div className="flex gap-2 bg-gray-800 rounded-xl p-1 border-2 border-gray-700">
              <button
                onClick={() => setViewMode('week')}
                className={`px-6 py-2 rounded-lg font-bold transition-all ${
                  viewMode === 'week'
                    ? 'bg-gradient-to-r from-yellow-400 to-yellow-500 text-black shadow-lg'
                    : 'text-gray-400 hover:text-white'
                }`}
              >
                Semaine
              </button>
              <button
                onClick={() => setViewMode('month')}
                className={`px-6 py-2 rounded-lg font-bold transition-all ${
                  viewMode === 'month'
                    ? 'bg-gradient-to-r from-yellow-400 to-yellow-500 text-black shadow-lg'
                    : 'text-gray-400 hover:text-white'
                }`}
              >
                Mois
              </button>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Navigation */}
        <div className="flex items-center justify-between mb-8 bg-gradient-to-r from-gray-800 to-gray-900 rounded-2xl p-6 border-2 border-gray-700">
          <button
            onClick={previousPeriod}
            className="p-3 bg-yellow-400 hover:bg-yellow-300 text-black rounded-xl transition-all hover:scale-110 shadow-lg"
          >
            <ChevronLeft className="w-6 h-6" />
          </button>
          
          <h2 className="text-2xl font-black text-white">
            {viewMode === 'week' 
              ? `Semaine du ${weekDays[0]?.toLocaleDateString('fr-CH', { day: 'numeric', month: 'long' })} au ${weekDays[6]?.toLocaleDateString('fr-CH', { day: 'numeric', month: 'long', year: 'numeric' })}`
              : currentDate.toLocaleDateString('fr-CH', { month: 'long', year: 'numeric' })
            }
          </h2>
          
          <button
            onClick={nextPeriod}
            className="p-3 bg-yellow-400 hover:bg-yellow-300 text-black rounded-xl transition-all hover:scale-110 shadow-lg"
          >
            <ChevronRight className="w-6 h-6" />
          </button>
        </div>

        {reservationsLoading ? (
          <div className="text-center py-20">
            <div className="inline-block w-16 h-16 border-4 border-yellow-400 border-t-transparent rounded-full animate-spin mb-4" />
            <div className="text-xl text-white">Chargement...</div>
          </div>
        ) : (
          <>
            {/* Week View */}
            {viewMode === 'week' && (
              <div className="grid grid-cols-7 gap-4">
                {weekDays.map((day, index) => {
                  const dayReservations = getReservationsForDay(new Date(day))
                  const isToday = day.toDateString() === new Date().toDateString()
                  
                  return (
                    <div key={index} className="flex flex-col">
                      <div className={`text-center py-4 rounded-t-2xl font-bold border-2 ${
                        isToday 
                          ? 'bg-gradient-to-r from-yellow-400 to-yellow-500 text-black border-yellow-400' 
                          : 'bg-gray-800 text-white border-gray-700'
                      }`}>
                        <div className="text-sm uppercase">
                          {day.toLocaleDateString('fr-CH', { weekday: 'short' })}
                        </div>
                        <div className="text-2xl font-black mt-1">
                          {day.getDate()}
                        </div>
                      </div>
                      
                      <div className="flex-1 bg-gradient-to-b from-gray-800 to-gray-900 border-2 border-t-0 border-gray-700 rounded-b-2xl p-2 min-h-[400px]">
                        <div className="space-y-2">
                          {dayReservations.map((reservation) => {
                            const vehicle = getVehicleById(reservation.vehicleId)
                            return (
                              <div
                                key={reservation._id}
                                className={`${getStatusColor(reservation.status)} rounded-xl p-3 border-2 shadow-lg hover:shadow-xl transition-all cursor-pointer`}
                              >
                                {vehicle && (
                                  <div className="mb-2">
                                    <img
                                      src={vehicle.imageUrl}
                                      alt={vehicle.name}
                                      className="w-full h-20 object-cover rounded-lg"
                                    />
                                  </div>
                                )}
                                
                                <div className="bg-black/60 rounded-lg p-2 text-white">
                                  <div className="flex items-center gap-1 mb-1">
                                    <Car className="w-3 h-3" />
                                    <p className="text-xs font-bold truncate">{reservation.vehicleName}</p>
                                  </div>
                                  
                                  {vehicle && (
                                    <p className="text-[10px] font-mono bg-yellow-400 text-black px-2 py-0.5 rounded inline-block mb-1">
                                      {vehicle.registration}
                                    </p>
                                  )}
                                  
                                  <div className="flex items-center gap-1 text-[10px]">
                                    <User className="w-3 h-3" />
                                    <span className="truncate">{reservation.clientName}</span>
                                  </div>
                                  
                                  <div className="flex items-center gap-1 text-[10px] mt-1">
                                    <Clock className="w-3 h-3" />
                                    <span>
                                      {new Date(reservation.startDate).toLocaleTimeString('fr-CH', { hour: '2-digit', minute: '2-digit' })}
                                      {' - '}
                                      {new Date(reservation.endDate).toLocaleTimeString('fr-CH', { hour: '2-digit', minute: '2-digit' })}
                                    </span>
                                  </div>
                                </div>
                              </div>
                            )
                          })}
                        </div>
                      </div>
                    </div>
                  )
                })}
              </div>
            )}

            {/* Month View */}
            {viewMode === 'month' && (
              <div>
                {/* Days Header */}
                <div className="grid grid-cols-7 gap-2 mb-2">
                  {['Lun', 'Mar', 'Mer', 'Jeu', 'Ven', 'Sam', 'Dim'].map((day) => (
                    <div key={day} className="text-center py-3 bg-gray-800 rounded-xl font-bold text-white border-2 border-gray-700">
                      {day}
                    </div>
                  ))}
                </div>
                
                {/* Calendar Grid */}
                <div className="grid grid-cols-7 gap-2">
                  {monthDays.map((dayObj, index) => {
                    const dayReservations = getReservationsForDay(new Date(dayObj.date))
                    const isToday = dayObj.date.toDateString() === new Date().toDateString()
                    
                    return (
                      <div
                        key={index}
                        className={`min-h-[120px] rounded-xl border-2 p-2 ${
                          isToday
                            ? 'bg-gradient-to-br from-yellow-400/20 to-yellow-500/10 border-yellow-400'
                            : dayObj.isCurrentMonth
                            ? 'bg-gradient-to-br from-gray-800 to-gray-900 border-gray-700'
                            : 'bg-gray-900/50 border-gray-800'
                        }`}
                      >
                        <div className={`text-right mb-1 font-bold ${
                          isToday
                            ? 'text-yellow-400 text-lg'
                            : dayObj.isCurrentMonth
                            ? 'text-white'
                            : 'text-gray-600'
                        }`}>
                          {dayObj.date.getDate()}
                        </div>
                        
                        <div className="space-y-1">
                          {dayReservations.slice(0, 2).map((reservation) => {
                            const vehicle = getVehicleById(reservation.vehicleId)
                            return (
                              <div
                                key={reservation._id}
                                className={`${getStatusColor(reservation.status)} rounded-lg p-1 border text-[10px] font-bold text-black hover:scale-105 transition-all cursor-pointer`}
                                title={`${reservation.vehicleName} - ${reservation.clientName}`}
                              >
                                <div className="flex items-center gap-1 truncate">
                                  <Car className="w-3 h-3 flex-shrink-0" />
                                  <span className="truncate">{vehicle?.registration || 'N/A'}</span>
                                </div>
                              </div>
                            )
                          })}
                          {dayReservations.length > 2 && (
                            <div className="text-[10px] text-center text-yellow-400 font-bold">
                              +{dayReservations.length - 2} autre(s)
                            </div>
                          )}
                        </div>
                      </div>
                    )
                  })}
                </div>
              </div>
            )}
          </>
        )}

        {/* Legend */}
        <div className="mt-8 bg-gradient-to-r from-gray-800 to-gray-900 rounded-2xl p-6 border-2 border-gray-700">
          <h3 className="text-lg font-black text-white mb-4 flex items-center gap-2">
            <Calendar className="w-5 h-5 text-yellow-400" />
            Légende des statuts
          </h3>
          <div className="grid grid-cols-2 md:grid-cols-6 gap-4">
            {[
              { status: 'pending', label: 'En attente' },
              { status: 'confirmed', label: 'Confirmée' },
              { status: 'paid', label: 'Payée' },
              { status: 'active', label: 'En cours' },
              { status: 'completed', label: 'Terminée' },
              { status: 'cancelled', label: 'Annulée' },
            ].map(({ status, label }) => (
              <div key={status} className="flex items-center gap-2">
                <div className={`w-6 h-6 rounded-lg ${getStatusColor(status)} border-2`} />
                <span className="text-sm text-gray-400">{label}</span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  )
}
